import ConduitEvents from '@tagboard/lib-events/dist/esm';

import {
  CONDUIT_PUSHER_AUTH_API,
  NODE_ENV,
  PUSHER_KEY,
  PUSHER_CLUSTER,
} from '@/config/appConfig';

import { storeToRefs } from 'pinia';
import useUserStore from '@/stores/user';

let instance;
let initPromise = null;

const ConduitEventsService = {
  init(userId) {
    // The Conduit client is user-scoped and the underlying lib-events client is
    // cached on `window`; re-initializing leaks a new Pusher connection, so
    // only initialize once per signed-in user (page loads reset the module).
    // Cache the in-flight promise so a layout remount during initialization
    // cannot start a second one; clear it on failure so a retry is possible.
    if (instance) return Promise.resolve(instance);

    if (!initPromise) {
      initPromise = ConduitEvents.initialize({
        pusherKey: PUSHER_KEY,
        pusherCluster: PUSHER_CLUSTER,
        pusherAuthApi: CONDUIT_PUSHER_AUTH_API,
        userId,
        eventLogging: (NODE_ENV || '').toLowerCase() !== 'production',
      }).then((initialized) => {
        instance = initialized;
        return initialized;
      }).catch((error) => {
        initPromise = null;
        throw error;
      });
    }

    return initPromise;
  },

  /**
   * @param {string} eventName - Format: `{source}.{action/category}.{eventName}` Example: 'producer.action.sportradar-element-added' or 'producer.event.production-went-live'
   * @param {object} eventData - The event data payload
   * @param {object} [eventContext] - Additional event context data
   * @returns {*}
   */
  track: (eventName, eventData, eventContext) => {
    const { currentTeamId, userId, isStaff } = storeToRefs(useUserStore());

    return instance?.track(eventName, eventData, {
      ownerId: currentTeamId.value,
      userId: userId.value,
      currentTeamId: currentTeamId.value,
      isStaff: isStaff.value,
      ...eventContext,
    });
  },

  Events: {
    // Poll Editor
    Polls_AddedRound: 'polls.added.round',
    Polls_AddedQuestion: 'polls.added.question',
    Polls_AddedDisplay: 'polls.added.display',
    Polls_ResetRound: 'polls.reset.round',
    Polls_ClickedAnalytics: 'polls.clicked.analytics',
    Polls_AddedElementToDisplay: 'polls.added.element-to-display',
    Polls_CopiedEmbedCode: 'polls.copied.embed-code',
    Polls_CopiedPageUrl: 'polls.copied.page-url',
    Polls_AddedToPage: 'polls.added.to-page',

    // Page Editor
    Pages_AddedElement: 'pages.element.added',
    Pages_EnabledPII: 'pages.element.enabled-pii',

    // Search
    Search_QuerySubmitted: 'search.query.submitted',
    Search_ResultClicked: 'search.result.clicked',
    Search_ResultsSeeAll: 'search.results.see-all',
    Search_ResultsViewMore: 'search.results.view-more',
    Search_FiltersApplied: 'search.filters.applied',
    Search_ActionsTagAdded: 'search.actions.tag-added',
    Search_ActionsDeleted: 'search.actions.deleted',

    // Show Editor
    Shows_Created: 'shows.created.show',
    Shows_Edited: 'shows.edited.show',
    Shows_AddedSegment: 'shows.added.segment',
    Shows_AddedMarker: 'shows.added.marker',
    Shows_AddedItem: 'shows.added.item',
  },
};

export default ConduitEventsService;
